import { useCallback, useState } from "react"
import { getAdditionalUserInfo, signInWithPopup } from "firebase/auth"

import { auth, googleProvider } from "@/api/firebase"
import { loginAndSetSessionExpiry } from "@/hooks/common/auth"
import { useCreateUser } from "@/hooks/user"

export interface GoogleSignInResult {
  success: boolean
  isNewUser?: boolean
  error?: unknown
}

/**
 * Hook for handling Google sign-in with automatic user creation.
 * Consolidates sign-in logic previously duplicated in LandingPage and LoginPage.
 */
export function useGoogleSignIn() {
  const { createUser } = useCreateUser()
  const [isSigningIn, setIsSigningIn] = useState(false)

  const signIn = useCallback(async (): Promise<GoogleSignInResult> => {
    if (isSigningIn) {
      return { success: false }
    }

    setIsSigningIn(true)

    try {
      const result = await signInWithPopup(auth, googleProvider)
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false

      // Set session TTL
      loginAndSetSessionExpiry()

      // Auto-create user record for new users
      if (isNewUser) {
        const displayName =
          result.user.displayName ||
          result.user.email?.split("@")[0] ||
          "User"
        const email = result.user.email || ""

        try {
          await createUser({ displayName, email })
        } catch (err) {
          console.error("Failed to create user:", err)
          return { success: false, isNewUser: true, error: err }
        }
      }

      return { success: true, isNewUser }
    } catch (err) {
      console.error("Sign in failed:", err)
      return { success: false, error: err }
    } finally {
      setIsSigningIn(false)
    }
  }, [isSigningIn, createUser])

  return { signIn, isSigningIn }
}
